import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  Fade,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { APIProvider } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";

import { Footer } from "../../../components/Footer/Footer";
import {
  buildPhlebotomyFinderTrackingContext,
  buildPhlebotomyFaqTrackingHandlers,
  trackPhlebotomySearchPageView,
  trackPhlebotomySearchPerformed,
  trackPhlebotomySearchClick,
  trackPhlebotomyReminderPageView,
  trackPhlebotomyReminderSubmitted,
  trackPhlebotomyReminderLinkClicked,
  PhlebotomySearchSource,
} from "../../../services/PhlebotomyTrackingService";
import {
  PhlebotomyApiService,
  PhlebotomySearchOrigin,
} from "../../../services/PhlebotomyApiService";
import { isPhlebotomySearchFailure } from "../../../services/PhlebotomySearchError";
import {
  PHLEBOTOMY_INTAKE_URL,
  PHLEBOTOMY_MOBILE_OPTIONS_URL,
  PHLEBOTOMY_SEARCH_URL,
} from "../../../utils/urlConstants";
import { PhlebotomyLocation } from "../types";
import PhlebotomyHeader from "../components/PhlebotomyHeader";
import PhlebotomyBackButton from "../components/PhlebotomyBackButton";
import PhlebotomyFAQ from "../components/PhlebotomyFAQ";
import PhlebotomyLocationFinderMap from "../Finder/PhlebotomyLocationFinderMap";

import PhlebotomyLocationList from "../Finder/PhlebotomyLocationList";
import { LocationCardAction } from "../Finder/PhlebotomyLocationCard";
import PhlebotomySearchAddressForm, {
  PhlebotomySearchAddressSelection,
} from "./PhlebotomySearchAddressForm";
import PhlebotomyIntakeForm, {
  PhlebotomyIntakeData,
} from "./PhlebotomyIntakeForm";
import MobilePhlebotomyRequestCard from "./MobilePhlebotomyRequestCard";
import {
  NEARBY_DISPLAY_RADIUS_MILES,
  SEARCH_RADIUS_MILES,
} from "./phlebotomySearchConstants";
import {
  captureInitialPhlebotomyCampaignEntry,
  claimPhlebotomyCampaignEntry,
  createAnonymousId,
  formatPhoneForApi,
  isUuid,
} from "./phlebotomyFormUtils";
import { resolvePhlebotomySearchEntryPath } from "./phlebotomySearchEntryPath";
import { usePhlebotomySalesforceFinderRollout } from "../../../utils/hooks/usePhlebotomySalesforceFinderRollout";
import {
  AddressPanel,
  AddressPanelContent,
  AddressSupport,
  AddressTitle,
  ChangeLocationButton,
  FartherResultsNotice,
  IntakePageBody,
  IntakePageContent,
  LoadingStatus,
  OriginDisplayLabel,
  ResultsControls,
  ResultsStatusContainer,
  SearchLayout,
  SearchPageContainer,
  MobilePromoSlot,
  MobileResultsContent,
  MobileResultsDragArea,
  MobileResultsBody,
  MobileResultsFooter,
  MobileResultsGrip,
  MobileResultsList,
  MobileResultsPanel,
  SidebarPromoSlot,
} from "./PhlebotomySearchPage.styles";
import {
  MapContainer,
  PageContainer,
  ResultsList,
} from "../components/PhlebotomySplitPane.styles";

const DEFAULT_SEARCH_MAP_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_SEARCH_MAP_ZOOM = 4;
const EMPTY_RESULTS_MAP_ZOOM = 9;
const CATALOG_FINDER_LEGACY_FALLBACK_TIMEOUT_MS = 5000;
const MOBILE_RESULTS_SNAP_DURATION_MS = 280;
const MOBILE_MAP_RESIZE_SETTLE_MS = 100;
const MOBILE_SHEET_MINIMIZED_HEIGHT = 72;

type SearchViewMode = "intake" | "address" | "results";
type MobileResultsSize = "half" | "minimized";
interface MobileResultsDragMetrics {
  maxOffset: number;
  minOffset: number;
  panelHeight: number;
}
interface PhlebotomySearchParams {
  portalAccountPublicId?: string;
}

/** Intake is `/blooddraw` (+ provider/account suffixes), not nested product
 *  routes like `/blooddraw/search` that share the same prefix. */
const BLOODDRAW_NON_INTAKE_FIRST_SEGMENTS = new Set([
  "search",
  "finder",
  "phone-options",
  "mobile-request",
]);

const isPhlebotomyIntakePath = (pathname: string): boolean => {
  if (pathname === PHLEBOTOMY_INTAKE_URL) {
    return true;
  }
  if (!pathname.startsWith(`${PHLEBOTOMY_INTAKE_URL}/`)) {
    return false;
  }
  const firstSegment = pathname
    .slice(PHLEBOTOMY_INTAKE_URL.length + 1)
    .split("/")[0];
  return !BLOODDRAW_NON_INTAKE_FIRST_SEGMENTS.has(firstSegment);
};

const isProviderIntakePath = (pathname: string): boolean =>
  pathname === `${PHLEBOTOMY_INTAKE_URL}/provider` ||
  pathname.startsWith(`${PHLEBOTOMY_INTAKE_URL}/provider/`);

interface SuccessfulSearch {
  origin: PhlebotomySearchOrigin;
  locations: PhlebotomyLocation[];
  searchCenter: { lat: number; lng: number };
  searchRadiusMiles: number | null;
  originDisplayLabel: string;
  searchSource: PhlebotomySearchSource;
}

interface FinderNavigationResult extends SuccessfulSearch {
  analyticsId: string;
  clinicAccountName: string | null;
}

interface PhlebotomySearchNavigationState {
  fromPhlebotomyReminder?: boolean;
  finderResult?: FinderNavigationResult;
  reminderTrackingId?: string;
  intakeContact?: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    estimatedDueDate?: string;
  };
}

const ignoreTrackingFailure = (tracking: Promise<void>) => {
  tracking.catch(() => undefined);
};

const hasNearbyLocation = (locations: PhlebotomyLocation[]): boolean =>
  locations.some(
    (location) =>
      location.distanceMiles != null &&
      location.distanceMiles <= NEARBY_DISPLAY_RADIUS_MILES,
  );

/** Account public IDs are eight alphanumeric characters. */
const PORTAL_ACCOUNT_PUBLIC_ID_RE = /^[A-Za-z0-9]{8}$/;

const PhlebotomySearchPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    contextKey: catalogFinderContextKey,
    isEnabled: catalogFinderEnabled,
    isResolved: isCatalogFinderFlagResolved,
  } = usePhlebotomySalesforceFinderRollout();
  const history = useHistory();
  const routerLocation = useLocation<PhlebotomySearchNavigationState>();
  const { portalAccountPublicId } = useParams<PhlebotomySearchParams>();
  const isTabletUp = useMediaQuery("(min-width: 768px)");
  const [mobileVisualViewportBottom, setMobileVisualViewportBottom] = useState<
    number | null
  >(null);
  const clinicPublicId = useMemo(() => {
    const queryClinic = new URLSearchParams(routerLocation.search).get(
      "clinic",
    );
    const candidate = portalAccountPublicId || queryClinic?.trim() || null;
    // Malformed clinic values reach us from unrendered campaign merge tags and
    // hand-edited URLs. Drop the scope and search universally rather than
    // sending a value the API will reject and stranding the patient.
    return candidate && PORTAL_ACCOUNT_PUBLIC_ID_RE.test(candidate)
      ? candidate
      : null;
  }, [portalAccountPublicId, routerLocation.search]);
  const deepLinkedZip = useMemo(() => {
    const zip = new URLSearchParams(routerLocation.search).get("zip")?.trim();
    return zip && /^\d{5}$/.test(zip) ? zip : null;
  }, [routerLocation.search]);
  const incomingTrackingId = useMemo(() => {
    const value = new URLSearchParams(routerLocation.search).get("tracking_id");
    return isUuid(value) ? value : null;
  }, [routerLocation.search]);
  const incomingCustomerIoUserId = useMemo(() => {
    const value = new URLSearchParams(routerLocation.search)
      .get("user_id")
      ?.trim();
    return value && value.length <= 255 ? value : null;
  }, [routerLocation.search]);
  const submittedBy = isProviderIntakePath(routerLocation.pathname)
    ? "provider"
    : "patient";

  const initialFinderResultRef = useRef<FinderNavigationResult | null>(
    routerLocation.state?.finderResult ?? null,
  );
  const initialFinderResult = initialFinderResultRef.current;
  const startedFromFinderNavigation = Boolean(initialFinderResult);
  const enteredThroughReminderRef = useRef(
    isPhlebotomyIntakePath(routerLocation.pathname),
  );
  const enteredThroughReminder = enteredThroughReminderRef.current;
  const reminderTrackingIdRef = useRef(
    incomingTrackingId ??
      (isUuid(routerLocation.state?.reminderTrackingId)
        ? routerLocation.state?.reminderTrackingId
        : createAnonymousId()),
  );
  const reminderTrackingId = reminderTrackingIdRef.current;
  const [campaignEntry] = useState(() =>
    captureInitialPhlebotomyCampaignEntry(routerLocation.search),
  );
  // Location-only searches must remain anonymous. This becomes active only
  // for an inbound reminder/campaign link or after a new reminder opt-in succeeds.
  const [activeReminderTrackingId, setActiveReminderTrackingId] = useState<
    string | null
  >(incomingTrackingId);
  // Fresh reminder/QR links need intake. Campaign returns already carry a
  // tracking_id from an enrolled reminder, so they skip signup and open the
  // finder (ZIP results when present, otherwise the location chooser).
  const needsReminderIntake = enteredThroughReminder && !incomingTrackingId;
  const [viewMode, setViewMode] = useState<SearchViewMode>(() => {
    if (initialFinderResult) return "results";
    if (needsReminderIntake) return "intake";
    if (deepLinkedZip) return "results";
    return "address";
  });
  const [locations, setLocations] = useState<PhlebotomyLocation[]>(
    initialFinderResult?.locations ?? [],
  );
  // Deep-linked ZIP results wait for the catalog rollout before searching.
  // Show the normal pending state rather than a blank results pane.
  const [loading, setLoading] = useState(
    () =>
      Boolean(deepLinkedZip) && !initialFinderResult && !needsReminderIntake,
  );
  const [error, setError] = useState<string | null>(null);
  const [originDisplayLabel, setOriginDisplayLabel] = useState<string | null>(
    initialFinderResult?.originDisplayLabel ?? null,
  );
  const [displayedSearchSource, setDisplayedSearchSource] =
    useState<PhlebotomySearchSource | null>(
      initialFinderResult?.searchSource ?? null,
    );
  const [hasSuccessfulSearch, setHasSuccessfulSearch] = useState(
    Boolean(initialFinderResult),
  );
  const [searchCenter, setSearchCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(initialFinderResult?.searchCenter ?? null);
  const [searchRadiusMiles, setSearchRadiusMiles] = useState<number | null>(
    initialFinderResult?.searchRadiusMiles ?? null,
  );
  const [selectedLocationIndex, setSelectedLocationIndex] = useState<
    number | null
  >(null);
  const [mobileResultsSize, setMobileResultsSize] =
    useState<MobileResultsSize>("half");
  const [isMobileResultsDragging, setIsMobileResultsDragging] = useState(false);
  const [isInlineLocationEditing, setIsInlineLocationEditing] = useState(false);
  const isMobileResultsOpen =
    mobileResultsSize !== "minimized" || isMobileResultsDragging;
  const mobileResultsDragStartYRef = useRef<number | null>(null);
  const mobileResultsDragActiveRef = useRef(false);
  const suppressMobileResultsClickRef = useRef(false);
  const mobileResultsPanelRef = useRef<HTMLDivElement>(null);
  const mobileResultsDragMetricsRef = useRef<MobileResultsDragMetrics | null>(
    null,
  );
  const mobileResultsSnapTimeoutRef = useRef<number | null>(null);
  const mobileResultsSnapCleanupRef = useRef<(() => void) | null>(null);
  const mobileResultsDragFrameRef = useRef<number | null>(null);
  const mobileResultsDragPendingHeightRef = useRef<number | null>(null);
  const mobileMarkerSelectionTimeoutRef = useRef<number | null>(null);
  const mobilePendingMarkerSelectionRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      mobileResultsSnapCleanupRef.current?.();
      if (mobileResultsDragFrameRef.current !== null) {
        window.cancelAnimationFrame(mobileResultsDragFrameRef.current);
      }
      if (mobileMarkerSelectionTimeoutRef.current !== null) {
        window.clearTimeout(mobileMarkerSelectionTimeoutRef.current);
      }
    },
    [],
  );
  const [clinicAccountName, setClinicAccountName] = useState<string | null>(
    initialFinderResult?.clinicAccountName ?? null,
  );
  const [intakeSubmitting, setIntakeSubmitting] = useState(false);
  const [intakeError, setIntakeError] = useState<string | null>(null);
  const [intakeContact, setIntakeContact] = useState(
    () =>
      routerLocation.state?.intakeContact ?? {
        firstName: "",
        lastName: "",
        phoneNumber: "",
        estimatedDueDate: "",
      },
  );
  // Keep a sync copy for history.push during performSearch — setState from
  // intake submit has not flushed yet when search navigates and remounts.
  const intakeContactRef = useRef(intakeContact);
  intakeContactRef.current = intakeContact;
  // Fresh reminder intake must reuse reminderTrackingId so page-view / submit /
  // search / click events join to the backend reminder and campaign-return traffic.
  // Location-only visits stay on a separate anonymous analytics ID.
  const [analyticsId] = useState(
    () =>
      initialFinderResult?.analyticsId ??
      incomingTrackingId ??
      incomingCustomerIoUserId ??
      (needsReminderIntake ? reminderTrackingId : createAnonymousId()),
  );
  const finderTrackingContext = useMemo(
    () =>
      buildPhlebotomyFinderTrackingContext({
        distinctId: analyticsId,
        clinicPublicId,
        userId: incomingCustomerIoUserId,
        trackingId: activeReminderTrackingId ?? undefined,
      }),
    [
      activeReminderTrackingId,
      analyticsId,
      clinicPublicId,
      incomingCustomerIoUserId,
    ],
  );
  const faqTrackingHandlers = useMemo(
    () => buildPhlebotomyFaqTrackingHandlers(finderTrackingContext),
    [finderTrackingContext],
  );
  const searchPageViewTrackedRef = useRef(false);
  const reminderPageViewTrackedRef = useRef(false);
  const [searchEntryPath] = useState(() =>
    resolvePhlebotomySearchEntryPath({
      enteredThroughReminder,
      incomingTrackingId,
      campaignEntry,
      fromPhlebotomyReminder: routerLocation.state?.fromPhlebotomyReminder,
    }),
  );
  /** Bumped on each search and Change location so only the latest request can commit. */
  const searchRequestIdRef = useRef(0);
  const lastSuccessfulSearchRef = useRef<SuccessfulSearch | null>(
    initialFinderResult
      ? {
          origin: initialFinderResult.origin,
          locations: initialFinderResult.locations,
          searchCenter: initialFinderResult.searchCenter,
          searchRadiusMiles: initialFinderResult.searchRadiusMiles ?? null,
          originDisplayLabel: initialFinderResult.originDisplayLabel,
          searchSource: initialFinderResult.searchSource,
        }
      : null,
  );
  const lastSearchRequestRef = useRef<{
    origin: PhlebotomySearchOrigin;
    originLabel: string;
    searchSource: PhlebotomySearchSource;
  } | null>(
    initialFinderResult
      ? {
          origin: initialFinderResult.origin,
          originLabel: initialFinderResult.originDisplayLabel,
          searchSource: initialFinderResult.searchSource,
        }
      : null,
  );
  const deepLinkSearchStartedRef = useRef(false);
  const [catalogFinderFallback, setCatalogFinderFallback] = useState(false);

  useEffect(() => {
    if (isCatalogFinderFlagResolved) {
      setCatalogFinderFallback(false);
      return;
    }

    const fallbackTimer = window.setTimeout(
      () => setCatalogFinderFallback(true),
      CATALOG_FINDER_LEGACY_FALLBACK_TIMEOUT_MS,
    );
    return () => window.clearTimeout(fallbackTimer);
  }, [isCatalogFinderFlagResolved]);

  const isCatalogFinderReady =
    isCatalogFinderFlagResolved || catalogFinderFallback;

  useEffect(() => {
    if (
      !needsReminderIntake ||
      routerLocation.state?.reminderTrackingId === reminderTrackingId
    ) {
      return;
    }

    // Keep the first-time opt-in ID private in browser history until the
    // patient enrolls. Back navigation and retries then reuse the same ID.
    history.replace({
      pathname: routerLocation.pathname,
      search: routerLocation.search,
      hash: routerLocation.hash,
      state: {
        ...routerLocation.state,
        reminderTrackingId,
      },
    });
  }, [
    history,
    needsReminderIntake,
    reminderTrackingId,
    routerLocation.hash,
    routerLocation.pathname,
    routerLocation.search,
    routerLocation.state,
  ]);

  useEffect(() => {
    // Old and current Customer.io campaign texts may still open the reminder
    // URL with a tracking_id. Those patients are already enrolled, so send
    // them to the canonical finder instead of the text-signup form.
    if (!enteredThroughReminder || !incomingTrackingId) {
      return;
    }

    const query = new URLSearchParams(routerLocation.search);
    query.set("tracking_id", incomingTrackingId);
    if (clinicPublicId) {
      query.set("clinic", clinicPublicId);
    } else {
      query.delete("clinic");
    }
    if (deepLinkedZip) {
      query.set("zip", deepLinkedZip);
    }
    const search = query.toString();
    history.replace({
      pathname: PHLEBOTOMY_SEARCH_URL,
      search: search ? `?${search}` : "",
    });
  }, [
    clinicPublicId,
    deepLinkedZip,
    enteredThroughReminder,
    history,
    incomingTrackingId,
    routerLocation.search,
  ]);

  useEffect(() => {
    if (isPhlebotomyIntakePath(routerLocation.pathname)) {
      // Campaign returns are redirected to the finder above; only fresh
      // enrollment links should reset into intake.
      if (incomingTrackingId) {
        return;
      }
      searchRequestIdRef.current += 1;
      setViewMode("intake");
      setLocations([]);
      setLoading(false);
      setError(null);
      setOriginDisplayLabel(null);
      setHasSuccessfulSearch(false);
      setSearchCenter(null);
      setSearchRadiusMiles(null);
      setDisplayedSearchSource(null);
      setSelectedLocationIndex(null);
      setClinicAccountName(null);
      setIntakeError(null);
      lastSuccessfulSearchRef.current = null;
      return;
    }

    const finderResult = routerLocation.state?.finderResult;
    if (routerLocation.pathname === PHLEBOTOMY_SEARCH_URL && finderResult) {
      setViewMode("results");
      setLocations(finderResult.locations);
      setLoading(false);
      setError(null);
      setOriginDisplayLabel(finderResult.originDisplayLabel);
      setHasSuccessfulSearch(true);
      setSearchCenter(finderResult.searchCenter);
      setSearchRadiusMiles(finderResult.searchRadiusMiles ?? null);
      setDisplayedSearchSource(finderResult.searchSource);
      setSelectedLocationIndex(null);
      setClinicAccountName(
        (current) => finderResult.clinicAccountName ?? current,
      );
      if (routerLocation.state?.intakeContact) {
        setIntakeContact(routerLocation.state.intakeContact);
      }
      lastSuccessfulSearchRef.current = {
        origin: finderResult.origin,
        locations: finderResult.locations,
        searchCenter: finderResult.searchCenter,
        searchRadiusMiles: finderResult.searchRadiusMiles ?? null,
        originDisplayLabel: finderResult.originDisplayLabel,
        searchSource: finderResult.searchSource,
      };
      lastSearchRequestRef.current = {
        origin: finderResult.origin,
        originLabel: finderResult.originDisplayLabel,
        searchSource: finderResult.searchSource,
      };
    }
  }, [incomingTrackingId, routerLocation.pathname, routerLocation.state]);

  /**
   * Canonical finder query string: inbound tracking ID, clinic scope, and the
   * ZIP that was searched. Every navigation into the finder or the mobile-draw
   * request form goes through this so a reload lands on the same results.
   */
  const buildFinderSearch = ({
    zip,
    trackingId = activeReminderTrackingId,
    clinicId = clinicPublicId,
  }: {
    zip?: string;
    trackingId?: string | null;
    clinicId?: string | null;
  }): string => {
    const query = new URLSearchParams(routerLocation.search);
    if (trackingId) {
      query.set("tracking_id", trackingId);
    } else {
      query.delete("tracking_id");
    }
    if (clinicId) {
      query.set("clinic", clinicId);
    } else {
      query.delete("clinic");
    }
    if (zip) {
      query.set("zip", zip);
    } else {
      query.delete("zip");
    }
    query.delete("scope");
    const search = query.toString();
    return search ? `?${search}` : "";
  };

  const restoreLastSuccessfulSearch = () => {
    const previous = lastSuccessfulSearchRef.current;
    if (!previous) return;

    setLocations(previous.locations);
    setSearchCenter(previous.searchCenter);
    setSearchRadiusMiles(previous.searchRadiusMiles);
    setOriginDisplayLabel(previous.originDisplayLabel);
    setDisplayedSearchSource(previous.searchSource);
    setHasSuccessfulSearch(true);
    setSelectedLocationIndex(null);
  };

  const performSearch = async (
    origin: PhlebotomySearchOrigin,
    originLabel: string,
    searchSource: PhlebotomySearchSource,
    trackingId = activeReminderTrackingId,
  ) => {
    const requestId = ++searchRequestIdRef.current;
    lastSearchRequestRef.current = { origin, originLabel, searchSource };
    setLoading(true);
    setError(null);
    setSelectedLocationIndex(null);
    setLocations([]);
    setSearchCenter(null);
    setSearchRadiusMiles(null);
    setOriginDisplayLabel(originLabel);
    setDisplayedSearchSource(searchSource);
    setViewMode("results");

    let searchOptions:
      | {
          finderSessionId?: string;
          portalAccountPublicId?: string;
          useSalesforceCatalog?: boolean;
        }
      | undefined;
    if (clinicPublicId) {
      searchOptions = { portalAccountPublicId: clinicPublicId };
    } else if (catalogFinderEnabled) {
      searchOptions = {
        useSalesforceCatalog: true,
        finderSessionId: catalogFinderContextKey,
      };
    }
    const canonicalZip =
      searchSource === "zip" && "zip" in origin ? origin.zip : undefined;

    try {
      const data = await PhlebotomyApiService.searchPhlebotomyLocations(
        origin,
        searchOptions,
      );
      if (requestId !== searchRequestIdRef.current) return;

      const nextCenter = {
        lat: data.search.latitude,
        lng: data.search.longitude,
      };
      const outsideNearbyBand =
        data.locations.length > 0 && !hasNearbyLocation(data.locations);

      setLocations(data.locations);
      setSearchCenter(nextCenter);
      setSearchRadiusMiles(data.searchRadiusMiles);
      setHasSuccessfulSearch(true);
      // Unknown and nonconfigured clinic scopes are answered as universal
      // searches. Use the response as the authority so stale query values are
      // removed from the canonical URL and subsequent actions.
      const effectiveClinicPublicId = data.portalAccount?.publicId ?? null;
      const nextClinicAccountName = data.portalAccount?.accountName ?? null;
      setClinicAccountName(nextClinicAccountName);
      setDisplayedSearchSource(searchSource);
      lastSuccessfulSearchRef.current = {
        origin,
        locations: data.locations,
        searchCenter: nextCenter,
        searchRadiusMiles: data.searchRadiusMiles,
        originDisplayLabel: originLabel,
        searchSource,
      };

      const finderResult: FinderNavigationResult = {
        origin,
        locations: data.locations,
        searchCenter: nextCenter,
        searchRadiusMiles: data.searchRadiusMiles,
        originDisplayLabel: originLabel,
        analyticsId,
        clinicAccountName: nextClinicAccountName,
        searchSource,
      };
      const canonicalSearch = buildFinderSearch({
        zip: canonicalZip,
        trackingId,
        clinicId: effectiveClinicPublicId,
      });

      if (history.location.pathname === routerLocation.pathname) {
        if (routerLocation.pathname !== PHLEBOTOMY_SEARCH_URL) {
          history.push({
            pathname: PHLEBOTOMY_SEARCH_URL,
            search: canonicalSearch,
            state: {
              fromPhlebotomyReminder: true,
              finderResult,
              intakeContact: intakeContactRef.current,
            },
          });
        } else {
          history.replace({
            pathname: PHLEBOTOMY_SEARCH_URL,
            search: canonicalSearch,
            state: {
              ...routerLocation.state,
              finderResult,
              intakeContact: intakeContactRef.current,
            },
          });
        }
      }

      ignoreTrackingFailure(
        trackPhlebotomySearchPerformed(
          buildPhlebotomyFinderTrackingContext({
            distinctId: analyticsId,
            clinicPublicId: effectiveClinicPublicId,
            userId: incomingCustomerIoUserId,
            trackingId: trackingId ?? undefined,
          }),
          searchSource,
          data.locations.length,
          outsideNearbyBand,
          searchEntryPath,
        ),
      );
    } catch (err) {
      if (requestId !== searchRequestIdRef.current) return;

      setLocations([]);
      setSearchCenter(null);
      setSearchRadiusMiles(null);
      setHasSuccessfulSearch(false);
      if (!isPhlebotomySearchFailure(err)) {
        setError(t("phlebotomySearch.errors.generic"));
      } else {
        switch (err.code) {
          case "invalid_zip":
            setError(t("phlebotomySearch.errors.zipInvalid"));
            break;
          case "zip_not_found":
            setError(t("phlebotomySearch.errors.zipNotFound"));
            break;
          case "geocoding_unavailable":
            setError(t("phlebotomySearch.errors.geocodingUnavailable"));
            break;
          case "account_not_found":
          case "invalid_account":
            setError(t("phlebotomySearch.errors.accountNotFound"));
            break;
          case "unknown":
            setError(t("phlebotomySearch.errors.generic"));
            break;
          default: {
            const exhaustiveCheck: never = err.code;
            setError(t("phlebotomySearch.errors.generic"));
            return exhaustiveCheck;
          }
        }
      }

      // Preserve the attempted ZIP and tracking ID even when lookup fails so
      // reload and retry stay on the finder instead of returning to intake.
      if (routerLocation.pathname !== PHLEBOTOMY_SEARCH_URL && canonicalZip) {
        history.push({
          pathname: PHLEBOTOMY_SEARCH_URL,
          search: buildFinderSearch({ zip: canonicalZip, trackingId }),
        });
      } else if (routerLocation.pathname === PHLEBOTOMY_SEARCH_URL) {
        history.replace({
          pathname: PHLEBOTOMY_SEARCH_URL,
          search: buildFinderSearch({
            zip: canonicalZip,
            trackingId,
          }),
          state: {
            ...routerLocation.state,
            finderResult: undefined,
          },
        });
      }
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Intake and map/results are separate Mixpanel page views. Never dual-fire
    // SearchPageView while intake is showing — that hid real map landings and
    // made SearchPageView useless for funnel splits.
    if (viewMode === "intake") {
      if (!reminderPageViewTrackedRef.current) {
        reminderPageViewTrackedRef.current = true;
        ignoreTrackingFailure(
          trackPhlebotomyReminderPageView(
            clinicPublicId || undefined,
            submittedBy,
            analyticsId,
          ),
        );
      }
      return;
    }

    if (!startedFromFinderNavigation && !searchPageViewTrackedRef.current) {
      searchPageViewTrackedRef.current = true;
      ignoreTrackingFailure(
        trackPhlebotomySearchPageView(finderTrackingContext, searchEntryPath),
      );
    }
  }, [
    analyticsId,
    clinicPublicId,
    finderTrackingContext,
    searchEntryPath,
    startedFromFinderNavigation,
    submittedBy,
    viewMode,
  ]);

  useEffect(() => {
    if (
      routerLocation.pathname !== PHLEBOTOMY_SEARCH_URL ||
      !campaignEntry ||
      !claimPhlebotomyCampaignEntry(campaignEntry)
    ) {
      return;
    }

    ignoreTrackingFailure(
      trackPhlebotomyReminderLinkClicked(
        buildPhlebotomyFinderTrackingContext({
          distinctId: analyticsId,
          clinicPublicId,
          userId: campaignEntry.userId ?? incomingCustomerIoUserId,
          trackingId: campaignEntry.trackingId,
        }),
      ),
    );
  }, [
    analyticsId,
    campaignEntry,
    clinicPublicId,
    incomingCustomerIoUserId,
    routerLocation.pathname,
  ]);

  useEffect(
    () => () => {
      searchRequestIdRef.current += 1;
    },
    [],
  );

  useEffect(() => {
    // Campaign returns redirect off the reminder URL before searching. Fresh
    // intake links search only after submit.
    if (
      routerLocation.pathname !== PHLEBOTOMY_SEARCH_URL ||
      initialFinderResult ||
      !deepLinkedZip ||
      !isCatalogFinderReady ||
      deepLinkSearchStartedRef.current
    ) {
      return;
    }

    deepLinkSearchStartedRef.current = true;
    performSearch({ zip: deepLinkedZip }, deepLinkedZip, "zip");
  }, [
    deepLinkedZip,
    initialFinderResult,
    isCatalogFinderReady,
    routerLocation.pathname,
  ]);

  const handleIntakeSubmit = async (intake: PhlebotomyIntakeData) => {
    setIntakeSubmitting(true);
    setIntakeError(null);

    try {
      const nextContact = {
        firstName: intake.firstName,
        lastName: intake.lastName,
        phoneNumber: intake.phoneNumber,
        estimatedDueDate: intake.estimatedDueDate,
      };
      intakeContactRef.current = nextContact;
      setIntakeContact(nextContact);
      let trackingIdForSearch = activeReminderTrackingId;
      if (intake.wantsTextReminders) {
        await PhlebotomyApiService.submitPhlebotomyReminder({
          first_name: intake.firstName,
          last_name: intake.lastName,
          phone_number: formatPhoneForApi(intake.phoneNumber),
          ...(intake.estimatedDueDate
            ? { estimated_due_date: intake.estimatedDueDate }
            : {}),
          postal_code: intake.zip,
          submitted_by: submittedBy,
          ...(clinicPublicId
            ? { portal_account_public_id: clinicPublicId }
            : {}),
          tracking_id: reminderTrackingId,
        });
        trackingIdForSearch = reminderTrackingId;
        setActiveReminderTrackingId(reminderTrackingId);

        ignoreTrackingFailure(
          trackPhlebotomyReminderSubmitted(
            clinicPublicId || undefined,
            submittedBy,
            analyticsId,
          ),
        );
      }
      await performSearch(
        { zip: intake.zip },
        intake.zip,
        "zip",
        trackingIdForSearch,
      );
    } catch {
      setIntakeError(t("phlebotomyIntake.errors.submission"));
    } finally {
      setIntakeSubmitting(false);
    }
  };

  const handleAddressSubmit = (selection: PhlebotomySearchAddressSelection) => {
    switch (selection.type) {
      case "zip":
        performSearch({ zip: selection.zip }, selection.zip, "zip");
        return;
      case "address":
      case "geolocation":
        performSearch(
          {
            lat: selection.lat,
            lng: selection.lng,
            ...(selection.zip ? { zip: selection.zip } : {}),
          },
          selection.displayLabel,
          selection.type,
        );
        return;
      default: {
        const exhaustiveCheck: never = selection;
        return exhaustiveCheck;
      }
    }
  };

  const handleInlineAddressSubmit = (
    selection: PhlebotomySearchAddressSelection,
  ) => {
    setIsInlineLocationEditing(false);
    handleAddressSubmit(selection);
  };

  useEffect(() => {
    const { visualViewport } = window;
    if (isTabletUp || !isInlineLocationEditing || !visualViewport) {
      setMobileVisualViewportBottom(null);
      return;
    }

    const updateVisualViewportBottom = () => {
      setMobileVisualViewportBottom(
        Math.round(visualViewport.offsetTop + visualViewport.height),
      );
    };

    updateVisualViewportBottom();
    visualViewport.addEventListener("resize", updateVisualViewportBottom);
    visualViewport.addEventListener("scroll", updateVisualViewportBottom);
    return () => {
      visualViewport.removeEventListener("resize", updateVisualViewportBottom);
      visualViewport.removeEventListener("scroll", updateVisualViewportBottom);
    };
  }, [isInlineLocationEditing, isTabletUp]);

  const handleChangeLocation = () => {
    ignoreTrackingFailure(
      trackPhlebotomySearchClick(finderTrackingContext, "change_location"),
    );
    if (!isTabletUp) {
      if (mobileResultsSize === "minimized") {
        setMobileResultsSize("half");
      }
      setIsInlineLocationEditing(true);
      return;
    }
    searchRequestIdRef.current += 1;
    setLoading(false);
    setError(null);
    restoreLastSuccessfulSearch();
    setViewMode("address");
  };

  const handleBackToResults = () => {
    ignoreTrackingFailure(
      trackPhlebotomySearchClick(finderTrackingContext, "back_to_results"),
    );
    setError(null);
    restoreLastSuccessfulSearch();
    setViewMode("results");
  };

  const physicalLocations = locations.filter(
    (location) => location.siteType !== "mobile_phlebotomy",
  );
  const lastSuccessfulSearch = lastSuccessfulSearchRef.current;

  const commitLocationSelection = (index: number) => {
    setSelectedLocationIndex(index);
    const location = physicalLocations[index];
    if (!location) return;
    ignoreTrackingFailure(
      trackPhlebotomySearchClick(finderTrackingContext, "location_selected", {
        location_name: location.name,
      }),
    );
  };

  const schedulePendingMobileMarkerSelection = () => {
    if (mobilePendingMarkerSelectionRef.current === null) return;

    if (mobileMarkerSelectionTimeoutRef.current !== null) {
      window.clearTimeout(mobileMarkerSelectionTimeoutRef.current);
    }
    mobileMarkerSelectionTimeoutRef.current = window.setTimeout(() => {
      mobileMarkerSelectionTimeoutRef.current = null;
      const pendingIndex = mobilePendingMarkerSelectionRef.current;
      mobilePendingMarkerSelectionRef.current = null;
      if (pendingIndex !== null) commitLocationSelection(pendingIndex);
    }, MOBILE_MAP_RESIZE_SETTLE_MS);
  };

  const handleLocationSelect = (index: number) => {
    if (mobileMarkerSelectionTimeoutRef.current !== null) {
      window.clearTimeout(mobileMarkerSelectionTimeoutRef.current);
      mobileMarkerSelectionTimeoutRef.current = null;
    }

    if (!isTabletUp && mobileResultsSize === "minimized") {
      mobilePendingMarkerSelectionRef.current = index;
      animateMobileResultsSnap("half", schedulePendingMobileMarkerSelection);
      return;
    }

    mobilePendingMarkerSelectionRef.current = null;
    commitLocationSelection(index);
  };

  const getMobileResultsLayout = () =>
    document.querySelector<HTMLElement>("[data-phlebotomy-layout]");

  const clearMobileResultsLayoutStyles = () => {
    const layout = getMobileResultsLayout();
    if (!layout) return;

    if (mobileResultsDragFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileResultsDragFrameRef.current);
      mobileResultsDragFrameRef.current = null;
    }
    mobileResultsDragPendingHeightRef.current = null;
    layout.style.removeProperty("--phlebotomy-mobile-sheet-height");
    layout.style.transition = "";
  };

  const getMobileResultsHeight = (size: MobileResultsSize) => {
    const layout = document.querySelector<HTMLElement>(
      "[data-phlebotomy-layout]",
    );
    if (!layout) return null;

    if (size === "minimized") {
      if (isInlineLocationEditing) {
        return getMobileResultsHeight("half");
      }
      return MOBILE_SHEET_MINIMIZED_HEIGHT;
    }

    return layout.getBoundingClientRect().height / 2;
  };

  const animateMobileResultsSnap = (
    size: MobileResultsSize,
    onComplete?: () => void,
  ) => {
    const panel = mobileResultsPanelRef.current;
    const targetHeight = getMobileResultsHeight(size);
    const layout = getMobileResultsLayout();
    if (!panel || !layout || targetHeight === null) {
      setMobileResultsSize(size);
      onComplete?.();
      return;
    }

    mobileResultsSnapCleanupRef.current?.();

    const currentHeight =
      mobileResultsDragPendingHeightRef.current ??
      panel.getBoundingClientRect().height;
    if (mobileResultsDragFrameRef.current !== null) {
      window.cancelAnimationFrame(mobileResultsDragFrameRef.current);
      mobileResultsDragFrameRef.current = null;
    }
    mobileResultsDragPendingHeightRef.current = null;
    layout.style.setProperty(
      "--phlebotomy-mobile-sheet-height",
      `${currentHeight}px`,
    );
    layout.getBoundingClientRect();
    layout.style.transition = `grid-template-rows ${MOBILE_RESULTS_SNAP_DURATION_MS}ms ease-out`;
    setMobileResultsSize(size);

    let finished = false;
    const finishSnap = (notifyComplete = true) => {
      if (finished) return;
      finished = true;
      layout.removeEventListener("transitionend", handleTransitionEnd);
      if (mobileResultsSnapTimeoutRef.current !== null) {
        window.clearTimeout(mobileResultsSnapTimeoutRef.current);
      }
      clearMobileResultsLayoutStyles();
      mobileResultsSnapTimeoutRef.current = null;
      mobileResultsSnapCleanupRef.current = null;
      if (notifyComplete) onComplete?.();
    };
    const handleTransitionEnd = (event: TransitionEvent) => {
      if (
        event.target === layout &&
        event.propertyName === "grid-template-rows"
      ) {
        finishSnap();
      }
    };
    layout.addEventListener("transitionend", handleTransitionEnd);
    mobileResultsSnapCleanupRef.current = () => finishSnap(false);

    window.requestAnimationFrame(() => {
      layout.style.removeProperty("--phlebotomy-mobile-sheet-height");
    });
    mobileResultsSnapTimeoutRef.current = window.setTimeout(
      finishSnap,
      MOBILE_RESULTS_SNAP_DURATION_MS + 120,
    );
  };

  const handleMobileResultsDragStart = (
    event: React.PointerEvent<HTMLElement>,
  ) => {
    if (isInlineLocationEditing) return;

    mobileResultsSnapCleanupRef.current?.();
    mobileResultsDragStartYRef.current = event.clientY;
    mobileResultsDragActiveRef.current = false;
    const panel = mobileResultsPanelRef.current;
    const map = document.querySelector<HTMLElement>("[data-phlebotomy-map]");
    if (!panel || !map) return;

    const panelBounds = panel.getBoundingClientRect();
    const minimumPanelHeight = getMobileResultsHeight("minimized") ?? 76;
    const maximumPanelHeight =
      getMobileResultsHeight("half") ?? panelBounds.height;
    mobileResultsDragMetricsRef.current = {
      panelHeight: panelBounds.height,
      minOffset: Math.min(0, panelBounds.height - maximumPanelHeight),
      maxOffset: Math.max(0, panelBounds.height - minimumPanelHeight),
    };
  };

  const handleMobileResultsDragMove = (
    event: React.PointerEvent<HTMLElement>,
  ) => {
    const startY = mobileResultsDragStartYRef.current;
    const metrics = mobileResultsDragMetricsRef.current;
    const panel = mobileResultsPanelRef.current;
    const layout = getMobileResultsLayout();
    if (startY === null || !metrics || !panel || !layout) return;

    if (Math.abs(event.clientY - startY) < 8) return;

    if (!mobileResultsDragActiveRef.current) {
      mobileResultsDragActiveRef.current = true;
      // Preserve ordinary taps until a genuine vertical drag begins. Moving
      // the grid row keeps the Google map and its attribution above the sheet.
      const panelBounds = panel.getBoundingClientRect();
      layout.style.transition = "none";
      layout.style.setProperty(
        "--phlebotomy-mobile-sheet-height",
        `${panelBounds.height}px`,
      );
      setIsMobileResultsDragging(true);
      event.currentTarget.setPointerCapture?.(event.pointerId);
    }

    const offset = Math.max(
      metrics.minOffset,
      Math.min(metrics.maxOffset, event.clientY - startY),
    );
    const height = metrics.panelHeight - offset;
    mobileResultsDragPendingHeightRef.current = height;
    if (mobileResultsDragFrameRef.current === null) {
      mobileResultsDragFrameRef.current = window.requestAnimationFrame(() => {
        const pendingHeight = mobileResultsDragPendingHeightRef.current;
        if (pendingHeight !== null) {
          layout.style.setProperty(
            "--phlebotomy-mobile-sheet-height",
            `${pendingHeight}px`,
          );
        }
        mobileResultsDragFrameRef.current = null;
      });
    }
  };

  const handleMobileResultsDragEnd = (
    event: React.PointerEvent<HTMLElement>,
  ) => {
    const startY = mobileResultsDragStartYRef.current;
    mobileResultsDragStartYRef.current = null;
    const metrics = mobileResultsDragMetricsRef.current;
    mobileResultsDragMetricsRef.current = null;
    if (startY === null) return;

    const didDrag = mobileResultsDragActiveRef.current;
    mobileResultsDragActiveRef.current = false;

    setIsMobileResultsDragging(false);

    if (!didDrag) {
      clearMobileResultsLayoutStyles();
      schedulePendingMobileMarkerSelection();
      return;
    }
    suppressMobileResultsClickRef.current = true;
    window.setTimeout(() => {
      suppressMobileResultsClickRef.current = false;
    }, 0);

    const minimumPanelHeight = getMobileResultsHeight("minimized") ?? 56;
    const maximumPanelHeight =
      getMobileResultsHeight("half") ?? metrics?.panelHeight ?? 0;
    const offset = metrics
      ? Math.max(
          metrics.minOffset,
          Math.min(metrics.maxOffset, event.clientY - startY),
        )
      : 0;
    const releasedHeight = (metrics?.panelHeight ?? 0) - offset;
    let targetSize: MobileResultsSize =
      releasedHeight >= (minimumPanelHeight + maximumPanelHeight) / 2
        ? "half"
        : "minimized";
    if (isInlineLocationEditing && targetSize === "minimized") {
      targetSize = "half";
    }
    if (mobilePendingMarkerSelectionRef.current !== null) {
      targetSize = "half";
    }
    animateMobileResultsSnap(
      targetSize,
      targetSize === "half" ? schedulePendingMobileMarkerSelection : undefined,
    );
  };

  const handleMobileLocationControlClick = () => {
    if (suppressMobileResultsClickRef.current) {
      suppressMobileResultsClickRef.current = false;
      return;
    }
    if (!isInlineLocationEditing) {
      handleChangeLocation();
    }
  };

  const cancelMobileResultsDrag = () => {
    mobileResultsDragStartYRef.current = null;
    mobileResultsDragMetricsRef.current = null;
    mobileResultsDragActiveRef.current = false;
    clearMobileResultsLayoutStyles();
    setIsMobileResultsDragging(false);
    schedulePendingMobileMarkerSelection();
  };

  const handleLocationButtonClick = (
    action: LocationCardAction,
    location: PhlebotomyLocation,
  ) => {
    ignoreTrackingFailure(
      trackPhlebotomySearchClick(finderTrackingContext, action, {
        location_name: location.name,
      }),
    );
  };

  const selectedLocation =
    selectedLocationIndex !== null
      ? physicalLocations[selectedLocationIndex]
      : null;

  const showResultsList = !loading && !error && physicalLocations.length > 0;
  const showEmptyResults =
    !loading && !error && hasSuccessfulSearch && physicalLocations.length === 0;
  const showFAQ = !loading && (hasSuccessfulSearch || Boolean(error));
  const showFartherNotice =
    showResultsList &&
    searchRadiusMiles !== null &&
    !hasNearbyLocation(physicalLocations);
  const isAddressEntry = viewMode === "address";
  const inlineInitialLocationValue =
    displayedSearchSource === "geolocation" ? "" : originDisplayLabel;
  const inlineInitialSelection: PhlebotomySearchAddressSelection | null =
    displayedSearchSource === "address" &&
    lastSuccessfulSearch?.searchSource === "address" &&
    "lat" in lastSuccessfulSearch.origin
      ? {
          type: "address",
          lat: lastSuccessfulSearch.origin.lat,
          lng: lastSuccessfulSearch.origin.lng,
          ...(lastSuccessfulSearch.origin.zip
            ? { zip: lastSuccessfulSearch.origin.zip }
            : {}),
          displayLabel: lastSuccessfulSearch.originDisplayLabel,
        }
      : null;
  const renderDividerLocationControl = () => (
    <Box
      data-phlebotomy-location-controls
      sx={{ width: "100%" }}
      onClick={handleMobileLocationControlClick}
    >
      <Collapse
        in={!isInlineLocationEditing}
        timeout={260}
        mountOnEnter
        unmountOnExit
      >
        <Fade in={!isInlineLocationEditing} timeout={180}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <ChangeLocationButton
              data-phlebotomy-location-search
              variant="text"
              startIcon={<SearchIcon />}
              aria-label={t("phlebotomySearch.search.changeLocation")}
            >
              {t("phlebotomySearch.search.changeLocation")}
            </ChangeLocationButton>
            {originDisplayLabel && (
              <OriginDisplayLabel variant="body2">
                {originDisplayLabel}
              </OriginDisplayLabel>
            )}
          </Box>
        </Fade>
      </Collapse>
      <Collapse
        in={isInlineLocationEditing}
        timeout={260}
        mountOnEnter
        unmountOnExit
      >
        <Fade in={isInlineLocationEditing} timeout={180}>
          <Box data-inline-location-search sx={{ pb: 1.5 }}>
            <PhlebotomySearchAddressForm
              inline
              initialLocationValue={inlineInitialLocationValue}
              initialSelection={inlineInitialSelection}
              onSubmit={handleInlineAddressSubmit}
            />
          </Box>
        </Fade>
      </Collapse>
    </Box>
  );
  const renderMobileResultsDragArea = () => (
    <MobileResultsDragArea
      data-mobile-results-drag-area
      style={
        isInlineLocationEditing
          ? { cursor: "auto", gap: "12px", touchAction: "auto" }
          : undefined
      }
      onPointerDown={handleMobileResultsDragStart}
      onPointerMove={handleMobileResultsDragMove}
      onPointerUp={handleMobileResultsDragEnd}
      onPointerCancel={cancelMobileResultsDrag}
    >
      <MobileResultsGrip data-mobile-results-grip aria-hidden="true" />
      {renderDividerLocationControl()}
    </MobileResultsDragArea>
  );
  const hasMobilePromo = !isTabletUp && hasSuccessfulSearch;
  let mobileOptionsZip = deepLinkedZip || "";
  if (lastSuccessfulSearch && "zip" in lastSuccessfulSearch.origin) {
    mobileOptionsZip = lastSuccessfulSearch.origin.zip ?? mobileOptionsZip;
  }

  const handleMobileOptions = () => {
    const latestFinderResult = lastSuccessfulSearchRef.current
      ? {
          ...lastSuccessfulSearchRef.current,
          analyticsId,
          clinicAccountName,
        }
      : routerLocation.state?.finderResult;
    const returnSearchState = latestFinderResult
      ? {
          ...routerLocation.state,
          finderResult: latestFinderResult,
        }
      : routerLocation.state;
    const navigationState = {
      analyticsId,
      mobileLocations: (
        lastSuccessfulSearchRef.current?.locations ?? locations
      ).filter((location) => location.siteType === "mobile_phlebotomy"),
      defaultFirstName: intakeContact.firstName,
      defaultLastName: intakeContact.lastName,
      defaultPhoneNumber: intakeContact.phoneNumber,
      defaultEstimatedDueDate: intakeContact.estimatedDueDate || undefined,
      returnSearchState,
      returnTo: `${routerLocation.pathname}${routerLocation.search}`,
    };
    const search = buildFinderSearch({
      zip: mobileOptionsZip || undefined,
    });

    ignoreTrackingFailure(
      trackPhlebotomySearchClick(
        finderTrackingContext,
        "mobile_phlebotomy_options_opened",
      ),
    );
    history.push({
      pathname: PHLEBOTOMY_MOBILE_OPTIONS_URL,
      search,
      state: navigationState,
    });
  };

  const mapLocationSource =
    isAddressEntry || error
      ? (lastSuccessfulSearchRef.current?.locations ?? locations)
      : locations;
  const mapLocations = useMemo(
    () =>
      mapLocationSource.filter(
        (location) => location.siteType !== "mobile_phlebotomy",
      ),
    [mapLocationSource],
  );
  const mapUserLocation =
    isAddressEntry || error
      ? (lastSuccessfulSearchRef.current?.searchCenter ?? searchCenter)
      : searchCenter;

  const renderResultsBody = () => {
    if (loading) {
      return (
        <ResultsStatusContainer role="status" aria-live="polite">
          <LoadingStatus>
            <CircularProgress size={32} />
            <Typography variant="h6" color="text.secondary" align="center">
              {t("phlebotomySearch.status.loading")}
            </Typography>
          </LoadingStatus>
        </ResultsStatusContainer>
      );
    }

    if (error) {
      return (
        <ResultsStatusContainer role="alert" aria-live="assertive">
          <Typography variant="h6" color="error" gutterBottom align="center">
            {t("phlebotomySearch.errors.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center">
            {error}
          </Typography>
        </ResultsStatusContainer>
      );
    }

    if (showResultsList) {
      return (
        <>
          {showFartherNotice && (
            <FartherResultsNotice
              variant="body2"
              role="status"
              aria-live="polite"
            >
              {t("phlebotomySearch.fartherResults.notice", {
                maxDistance: searchRadiusMiles,
              })}
            </FartherResultsNotice>
          )}
          <PhlebotomyLocationList
            locations={physicalLocations}
            selectedLocationIndex={selectedLocationIndex}
            onLocationSelect={handleLocationSelect}
            onButtonClick={handleLocationButtonClick}
          />
        </>
      );
    }

    if (showEmptyResults) {
      return (
        <ResultsStatusContainer role="status" aria-live="polite">
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            align="center"
          >
            {t("phlebotomySearch.empty.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center">
            {t(
              clinicPublicId
                ? "phlebotomySearch.empty.clinicSuggestion"
                : "phlebotomySearch.empty.suggestion",
              { maxDistance: SEARCH_RADIUS_MILES },
            )}
          </Typography>
        </ResultsStatusContainer>
      );
    }

    return null;
  };

  const clinicContextLabel = clinicPublicId
    ? t("phlebotomySearch.clinicContext.recommendedLabel", {
        clinicName:
          clinicAccountName || t("phlebotomySearch.clinicContext.yourClinic"),
      })
    : null;
  const entryTitleKey:
    | "phlebotomySearch.page.enterNewAddressTitle"
    | "phlebotomySearch.page.enterAddressTitle" = hasSuccessfulSearch
    ? "phlebotomySearch.page.enterNewAddressTitle"
    : "phlebotomySearch.page.enterAddressTitle";

  if (viewMode === "intake") {
    return (
      <PageContainer>
        <PhlebotomyHeader />
        <IntakePageBody>
          <IntakePageContent>
            <AddressTitle component="h1">
              {t("phlebotomyIntake.page.title")}
            </AddressTitle>
            <AddressSupport>
              {t("phlebotomyIntake.page.support")}
            </AddressSupport>
            <PhlebotomyIntakeForm
              isSubmitting={intakeSubmitting}
              submissionError={intakeError}
              submittedBy={submittedBy}
              onSubmit={handleIntakeSubmit}
            />
          </IntakePageContent>
        </IntakePageBody>
        <Footer showLanguageSwitcher requireLanguageFeatureFlag={false} />
      </PageContainer>
    );
  }

  return (
    <APIProvider
      apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ""}
      libraries={["places"]}
    >
      <SearchPageContainer
        className={
          !isTabletUp && !isAddressEntry
            ? "phlebotomy-mobile-finder-viewport"
            : undefined
        }
        lockViewport={!isTabletUp && !isAddressEntry}
        style={
          mobileVisualViewportBottom === null
            ? undefined
            : ({
                "--phlebotomy-visual-viewport-bottom": `${mobileVisualViewportBottom}px`,
              } as React.CSSProperties)
        }
      >
        <PhlebotomyHeader subtitle={clinicContextLabel ?? undefined} />
        <SearchLayout
          data-phlebotomy-layout
          hasResults={showResultsList}
          isAddressEntry={isAddressEntry}
          hasClinicContext={Boolean(clinicContextLabel)}
          hasMobilePromo={hasMobilePromo}
          mobileResultsSize={mobileResultsSize}
        >
          {isAddressEntry ? (
            <AddressPanel>
              <AddressPanelContent>
                {hasSuccessfulSearch && (
                  <PhlebotomyBackButton onClick={handleBackToResults} />
                )}
                <AddressTitle component="h2">{t(entryTitleKey)}</AddressTitle>
                <AddressSupport>
                  {t("phlebotomySearch.page.enterAddressSupport")}
                </AddressSupport>
                <PhlebotomySearchAddressForm onSubmit={handleAddressSubmit} />
              </AddressPanelContent>
            </AddressPanel>
          ) : (
            <>
              {hasSuccessfulSearch && !isTabletUp && (
                <MobilePromoSlot>
                  <MobilePhlebotomyRequestCard
                    onRequest={handleMobileOptions}
                  />
                </MobilePromoSlot>
              )}
              {isTabletUp ? (
                <>
                  <ResultsControls data-phlebotomy-results-controls>
                    {hasSuccessfulSearch && (
                      <SidebarPromoSlot>
                        <MobilePhlebotomyRequestCard
                          onRequest={handleMobileOptions}
                        />
                      </SidebarPromoSlot>
                    )}
                    <ChangeLocationButton
                      variant="text"
                      startIcon={<SearchIcon />}
                      onClick={handleChangeLocation}
                    >
                      {t("phlebotomySearch.search.changeLocation")}
                    </ChangeLocationButton>
                    {originDisplayLabel && (
                      <OriginDisplayLabel variant="body2">
                        {originDisplayLabel}
                      </OriginDisplayLabel>
                    )}
                  </ResultsControls>
                  <ResultsList data-phlebotomy-results-scroll>
                    {renderResultsBody()}
                    {showFAQ && <PhlebotomyFAQ {...faqTrackingHandlers} />}
                  </ResultsList>
                </>
              ) : (
                <MobileResultsPanel
                  ref={mobileResultsPanelRef}
                  data-mobile-results-size={mobileResultsSize}
                  mobileResultsSize={mobileResultsSize}
                  style={
                    isInlineLocationEditing ? { overflowY: "auto" } : undefined
                  }
                >
                  <>
                    <ResultsControls
                      data-phlebotomy-results-controls
                      style={
                        isInlineLocationEditing
                          ? {
                              alignItems: "stretch",
                              flex: "0 0 auto",
                            }
                          : undefined
                      }
                    >
                      {renderMobileResultsDragArea()}
                    </ResultsControls>
                    {isMobileResultsOpen && (
                      <MobileResultsContent id="phlebotomy-mobile-results">
                        <MobileResultsList data-phlebotomy-results-scroll>
                          <MobileResultsBody>
                            {renderResultsBody()}
                            {showFAQ && (
                              <PhlebotomyFAQ {...faqTrackingHandlers} />
                            )}
                          </MobileResultsBody>
                          <MobileResultsFooter>
                            <Footer
                              showLanguageSwitcher
                              requireLanguageFeatureFlag={false}
                            />
                          </MobileResultsFooter>
                        </MobileResultsList>
                      </MobileResultsContent>
                    )}
                  </>
                </MobileResultsPanel>
              )}
            </>
          )}
          <MapContainer data-phlebotomy-map isAddressEntry={isAddressEntry}>
            <PhlebotomyLocationFinderMap
              locations={mapLocations}
              userLocation={mapUserLocation}
              selectedLocation={isAddressEntry ? null : selectedLocation}
              onMarkerClick={handleLocationSelect}
              onInfoWindowClose={() => setSelectedLocationIndex(null)}
              defaultCenter={DEFAULT_SEARCH_MAP_CENTER}
              defaultZoom={DEFAULT_SEARCH_MAP_ZOOM}
              emptyLocationsZoom={EMPTY_RESULTS_MAP_ZOOM}
              preferNearbyRadiusMiles={NEARBY_DISPLAY_RADIUS_MILES}
            />
          </MapContainer>
        </SearchLayout>
        {(isTabletUp || isAddressEntry) && (
          <Footer showLanguageSwitcher requireLanguageFeatureFlag={false} />
        )}
      </SearchPageContainer>
    </APIProvider>
  );
};

export default PhlebotomySearchPage;
